import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useFamily } from "@/hooks/useFamily";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent } from "@/components/ui/card";
import { getDateString } from "@/lib/family-date";

interface Child {
  id: string;
  name: string;
  avatar_emoji: string;
  family_id: string;
}

interface Props {
  children: Child[];
}

export function CustodySummary({ children }: Props) {
  const { memberships, timezone } = useFamily();
  const { user } = useAuth();
  const todayStr = getDateString(new Date(), timezone);
  const familyIds = memberships.map((m) => m.family_id);

  const { data: todayDays = [] } = useQuery({
    queryKey: ["current-custody", familyIds.join(","), todayStr],
    queryFn: async () => {
      if (familyIds.length === 0) return [];
      const { data, error } = await supabase
        .from("custody_days")
        .select("*")
        .in("family_id", familyIds)
        .eq("date", todayStr);
      if (error) throw error;
      return data ?? [];
    },
    enabled: familyIds.length > 0,
  });

  if (todayDays.length === 0) return null;

  return (
    <Card>
      <CardContent className="p-4">
        <p className="text-sm font-medium text-muted-foreground mb-2">
          I dag hos
        </p>
        <div className="space-y-1">
          {todayDays.map((day: any) => {
            const child = children.find((c) => c.id === day.child_id);
            if (!child) return null;
            const isMe = day.custodial_parent_user_id === user?.id;
            return (
              <p key={day.id} className="text-sm">
                {child.avatar_emoji} {child.name} — {isMe ? "Deg" : "Medforelder"}
                {day.is_handover && " 🔁"}
              </p>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
